import { selectFromCaptureTree } from "@/components/ui/tree/cad-model-tree/use-disable-capture-tree-alignment";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import {
  isIElementSiteScapeCapture,
  selectSiteScapeScanChildren,
} from "@/modes/export-mode/scans-export/scans-export-sitescape-utils";
import { useCurrentScene } from "@/modes/mode-data-context";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { selectLightweightElementsSlice } from "@/store/lightweight-elements-selectors";
import { curryAppSelector } from "@/store/reselect";
import { selectActiveElement } from "@/store/selections-selectors";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { generateClusterTree } from "@/utils/tree-generation/generate-cluster-tree";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { generateProjectTree } from "@/utils/tree-generation/tree-generation";
import { assert, GUID, walkWithQueue } from "@faro-lotv/foundation";
import {
  IElement,
  IElementOdometryPathSection,
  IElementTypeHint,
  isIElementCluster,
  isIElementOdometryPathSection,
  isIElementSectionDataSetCapture,
  isIElementSectionDataSetEls,
  isIElementSectionDataSetFocus,
  isIElementSectionDataSetOrbis,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectClusters,
  selectIElement,
} from "@faro-lotv/project-source";
import { CaptureTreeEntityType } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";

type State = Pick<RootState, "lightweightElements" | "iElements">;

/**
 * @returns A selector to get the dataset section ancestor of a given element from capture tree
 * which is supported for scans export
 * @param state - Root state of the application
 * @param element - Element whose dataset section ancestor is to be found
 */
export const selectScansExportableDatasetSection = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElementsSlice,
      (state: State, element: IElement) => element,
    ],
    (iElements, lightweightElements, element) => {
      const datasetSection = selectAncestor(
        element,
        (el) =>
          isIElementSectionDataSetFocus(el) ||
          isIElementSectionDataSetEls(el) ||
          isIElementSectionDataSetOrbis(el) ||
          isIElementSectionDataSetCapture(el),
      )({ iElements });

      const isCaptureTreeDatasetSection = selectFromCaptureTree(datasetSection)(
        { lightweightElements },
      );

      if (datasetSection && isCaptureTreeDatasetSection) {
        return datasetSection;
      }
    },
  ),
);

const selectFilteredPaths = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, datasetSectionId: GUID) => datasetSectionId,
      (
        state: RootState,
        datasetSectionId: GUID,
        paths: IElementOdometryPathSection[],
      ) => paths,
    ],
    (iElements, datasetSectionId, paths) =>
      paths.filter(
        (path) =>
          !!selectAncestor(
            path,
            (el) => el.id === datasetSectionId,
          )({ iElements }),
      ),
  ),
);

const selectFilteredPanos = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, datasetSectionId: GUID) => datasetSectionId,
      (state: RootState, datasetSectionId: GUID, panos: IElementWaypoint[]) =>
        panos,
    ],
    (iElements, datasetSectionId, panos) =>
      panos.filter(
        (pano) =>
          !!selectAncestor(
            pano,
            (el) => el.id === datasetSectionId,
          )({ iElements }),
      ),
  ),
);

const selectScanToPanoPathMap = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, panos: IElementWaypoint[]) => panos,
      (
        state: RootState,
        panos: IElementWaypoint[],
        paths: IElementOdometryPathSection[],
      ) => paths,
      (
        state: RootState,
        panos: IElementWaypoint[],
        paths: IElementOdometryPathSection[],
        scans: Map<GUID, IElement>,
      ) => scans,
    ],
    (iElements, panos, paths, scans) => {
      const scanToPanoPathMap = {
        forward: new Map(),
        reverse: new Map<GUID, GUID>(),
      };
      for (const pano of panos) {
        const ancestor = selectAncestor(pano, (el) => scans.has(el.id))({
          iElements,
        });

        assert(
          ancestor,
          `Pano ${pano.id} should have a scan ancestor in the given scans list`,
        );
        scanToPanoPathMap.forward.set(ancestor.id, pano.id);
        scanToPanoPathMap.reverse.set(pano.id, ancestor.id);
      }

      for (const path of paths) {
        const ancestor = selectAncestor(path, (el) => scans.has(el.id))({
          iElements,
        });

        assert(
          ancestor,
          `Odometry path ${path.id} should have a scan ancestor in the given scans list`,
        );
        scanToPanoPathMap.forward.set(ancestor.id, path.id);
        scanToPanoPathMap.reverse.set(path.id, ancestor.id);
      }

      return scanToPanoPathMap;
    },
  ),
);

/**
 * Collects IDs of cluster nodes that are considered invalid for export.
 *
 * @param state - The root state of the application
 * @param tree - The cluster tree to check
 * @returns A set of cluster IDs that are invalid for export
 */
const selectInvalidClusterIds = curryAppSelector(
  createSelector(
    [selectIElementsSlice, (state: RootState, tree: TreeData[]) => tree],
    (iElements, tree) => {
      const invalidIds = new Set<GUID>();

      // Check if the cluster has exportable scans and it is not an empty cluster
      // Also checks recursively if the children clusters have exportable scans, and if not, add them to the invalid set as well
      function hasExportableScans(nodes: TreeData[]): boolean {
        return nodes.reduce<boolean>((found, node) => {
          const element = selectIElement(node.element?.id)({ iElements });
          if (
            element &&
            isIElementSiteScapeCapture(element) &&
            selectSiteScapeScanChildren(element)({ iElements }).length > 0
          ) {
            return true;
          }
          if (node.element?.type !== CaptureTreeEntityType.cluster) return true;
          if (node.children && hasExportableScans(node.children)) return true;
          invalidIds.add(node.id);
          return found;
        }, false);
      }

      hasExportableScans(tree);
      return invalidIds;
    },
  ),
);

const selectClusterTreeData = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectClusters,
      (state: RootState, datasetSection: IElement) => datasetSection,
    ],
    (iElementsState, clusters, datasetSection) => {
      const { iElements } = iElementsState;

      // Cluster-based tree structure
      const clusterRoot = Object.values(clusters).find(
        (cluster) => cluster?.type === CaptureTreeEntityType.root,
      );

      assert(
        clusterRoot,
        "Root cluster should always exist in the project tree",
      );

      const hasClustersStructure = datasetSection.childrenIds?.some(
        (id) => iElements[id] && isIElementCluster(iElements[id]),
      );

      const clusterTree = hasClustersStructure
        ? generateProjectTree({
            parentId: datasetSection.id,
            elements: iElements,
            clusters,
          })
        : generateClusterTree({
            clusterElement: clusterRoot,
            clusters,
            dataset: datasetSection,
            elements: iElements,
          });

      // Filter out the scan sections that are not supported by structured export
      // i.e scanWs (WebShare scan)
      const filteredClusterTree = clusterTree.filter((node) => {
        const element = iElements[node.id];

        return element?.typeHint !== IElementTypeHint.scanWs;
      });

      // Collect all scans within the cluster tree, skipping empty clusters
      // that appear as leaves (but keeping SiteScape captures which are
      // clusters that need to be shown as markers)
      const scans = new Map<GUID, IElement>();
      walkWithQueue([...filteredClusterTree], (node, append) => {
        if (!node.children) {
          const element = iElements[node.id];
          if (
            element &&
            (!isIElementCluster(element) || isIElementSiteScapeCapture(element))
          ) {
            scans.set(element.id, element);
          }
        }
        append(...(node.children ?? []));
      });

      return { clusterTree: filteredClusterTree, scans };
    },
  ),
);

interface UsePrepareScansExportDataReturn {
  /** The dataset section element which is the parent for all the children scans */
  datasetSection: IElement;

  /** The odometry paths if the given dataset contains any (i.e if the dataset is an orbis dataset in the capture tree) */
  paths: IElementOdometryPathSection[];

  /** The waypoints (panos) for the given dataset */
  panos: IElementWaypoint[];

  /** The cluster structure with scans as a tree */
  clusterTree: TreeData[];

  /** A map between scan IDs and their corresponding IElement objects */
  scans: Map<GUID, IElement>;

  /** IDs of cluster nodes that are invalid for export (e.g. contain no exportable scans) */
  invalidClusterIds: Set<GUID>;

  /** A map between scans and their corresponding pano/path IDs */
  scanToPanoPathMap: {
    forward: Map<GUID, GUID>;
    reverse: Map<GUID, GUID>;
  };

  /** Scans that do not have an associated pano or path (e.g. SiteScape, Photogrammetry) */
  scansWithoutPanoPath: IElement[];

  /**
   * Individual scan IElements to render as markers in the scene.
   * SiteScape clusters are expanded into their child scans; other orphan scans are included as-is.
   */
  markerScans: IElement[];

  /**
   * Maps a marker scan ID to the scan/cluster ID used for tree selection.
   * For SiteScape children this maps to the cluster ID; for others it's an identity mapping.
   */
  markerScanToTreeScanMap: Map<GUID, GUID>;
}

/**
 * Expands scans without pano/path for marker rendering.
 * SiteScape clusters are expanded into their individual child scans,
 * while other orphan scans (e.g. Photogrammetry) are kept as-is.
 *
 * @param state - The root state of the application
 * @param scansWithoutPanoPath - Scans that have no associated pano or path
 * @returns markerScans and markerScanToTreeScanMap for rendering and selection mapping
 */
const selectMarkerScansData = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, scansWithoutPanoPath: IElement[]) =>
        scansWithoutPanoPath,
    ],
    (iElements, scansWithoutPanoPath) => {
      const markerScans: IElement[] = [];
      const markerScanToTreeScanMap = new Map<GUID, GUID>();

      for (const scan of scansWithoutPanoPath) {
        if (isIElementSiteScapeCapture(scan)) {
          const children = selectSiteScapeScanChildren(scan)({ iElements });
          for (const child of children) {
            markerScans.push(child);
            markerScanToTreeScanMap.set(child.id, scan.id);
          }
        } else {
          markerScans.push(scan);
          markerScanToTreeScanMap.set(scan.id, scan.id);
        }
      }

      return { markerScans, markerScanToTreeScanMap };
    },
  ),
);

/** @returns the required data for working with scans export in the given scene based on the main element */
export function usePrepareScansExportData(): UsePrepareScansExportDataReturn {
  const activeElement = useAppSelector(selectActiveElement);
  assert(activeElement, "The scans export should have a main element");

  const datasetSection = useAppSelector(
    selectScansExportableDatasetSection(activeElement),
  );
  assert(
    datasetSection,
    "The main element should have a dataset section ancestor within capture tree",
  );

  const { paths, panos } = useCurrentScene();

  const pathSections = useMemo(
    () => paths.filter(isIElementOdometryPathSection),
    [paths],
  );

  const filteredPaths = useAppSelector(
    selectFilteredPaths(datasetSection.id, pathSections),
  );

  const filteredPanos = useAppSelector(
    selectFilteredPanos(datasetSection.id, panos),
  );

  const { clusterTree, scans } = useAppSelector(
    selectClusterTreeData(datasetSection),
  );

  const invalidClusterIds = useAppSelector(
    selectInvalidClusterIds(clusterTree),
  );

  const scanToPanoPathMap = useAppSelector(
    selectScanToPanoPathMap(filteredPanos, filteredPaths, scans),
  );

  const scansWithoutPanoPath = useMemo(
    () =>
      [...scans.values()].filter(
        (scan) => !scanToPanoPathMap.forward.has(scan.id),
      ),
    [scans, scanToPanoPathMap.forward],
  );

  const { markerScans, markerScanToTreeScanMap } = useAppSelector(
    selectMarkerScansData(scansWithoutPanoPath),
  );

  return {
    datasetSection,
    paths: filteredPaths,
    panos: filteredPanos,
    clusterTree,
    scans,
    invalidClusterIds,
    scanToPanoPathMap,
    scansWithoutPanoPath,
    markerScans,
    markerScanToTreeScanMap,
  };
}

/** @returns whether the current scene has a valid dataset for scans export */
export function useIsExportableDataset(): boolean {
  const activeElement = useAppSelector(selectActiveElement);

  const datasetSection = useAppSelector((state) =>
    activeElement
      ? selectScansExportableDatasetSection(activeElement)(state)
      : undefined,
  );

  return !!datasetSection;
}
